/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import { CONTAINER } from './container.constants.tsx';
import type { RankViewRequest } from './TypesRemoteViewRemover.tsx';
import type { NavigationView, Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import { findView, updateContainerState } from './updateContainerState.tsx';

type RankPayload = {
	id: string;
	parentId?: string;
	srcParentId?: string;
	index: number;
	isExpandedBeforeDrag: boolean;
};

export const rankView = (
	rankPayload: RankPayload,
	onActionSuccess?: () => void,
	onActionError?: (err?: Error) => void,
): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ getState, setState }: StoreActionApi<State>,
			{ containerId, cloudId, navigationRemote, onFailure, onSuccess, containerType }: Props,
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			const { id, index, parentId } = rankPayload;

			if (!parentId) {
				return;
			}

			experience.navBar.changeViewRank.start();

			// Save the original views to revert the changes in case of failure
			const previousViewsState = getState().containers[containerId].views;

			try {
				const { view: viewToMove, parent: viewToMoveParent } = findView(
					getState().containers[containerId],
					id,
				);

				if (!viewToMove) {
					return;
				}

				const newViews = previousViewsState
					.filter((view) => view.localId !== id)
					.map((view) => {
						if (viewToMoveParent && viewToMoveParent.localId === view.localId) {
							return {
								...view,
								views: view.views?.filter((v) => v.localId !== id),
							};
						}
						return view;
					});

				const viewToInsert = { ...viewToMove, isExpanded: rankPayload.isExpandedBeforeDrag };

				let moreAfterView: NavigationView | undefined;
				let moreBeforeView;
				let moveInEmptySet = false;
				if (parentId === 'root') {
					moreAfterView = newViews[index - 1];
					moreBeforeView = index === 0 ? newViews[index] : undefined;
					newViews.splice(index, 0, viewToInsert);
				} else {
					newViews.map((view) => {
						if (view.localId === parentId) {
							if (!view.views?.length) {
								moveInEmptySet = true;
								if (view.views === undefined) {
									Object.assign(view, {
										views: [],
									});
								}
							} else {
								moreAfterView = view.views?.[index - 1];
								moreBeforeView = index === 0 ? view.views?.[index] : undefined;
							}
							view.views?.splice(index, 0, viewToInsert);
							return { ...view };
						}
						return view;
					});
				}

				setState(
					updateContainerState(getState(), containerId, { views: newViews, isLocked: true }),
				);

				const parent =
					parentId === 'root'
						? containerId
						: newViews.find((view) => {
								if (view.localId === parentId) {
									return view;
								}
								return undefined;
							})?.id;

				if (!parent || (!moreAfterView && !moreBeforeView && !moveInEmptySet)) {
					return;
				}

				const parentType: RankViewRequest['parentType'] =
					parentId === 'root' ? containerType : CONTAINER.SECTION;

				const rankViewRequest: RankViewRequest = {
					containerId,
					id: viewToMove.id,
					parentType,
					parentId: parent,
					rankIndex: index,
					moveAfter: undefined,
					moveBefore: undefined,
				};

				if (moreAfterView) {
					rankViewRequest.moveAfter = {
						id: moreAfterView.id,
						viewType: moreAfterView.viewType,
					};
				} else {
					delete rankViewRequest.moveAfter;
				}

				if (moreBeforeView) {
					rankViewRequest.moveBefore = {
						id: moreBeforeView.id,
						viewType: moreBeforeView.viewType,
					};
				} else {
					delete rankViewRequest.moveBefore;
				}

				await navigationRemote?.rankView(rankViewRequest);

				setState(updateContainerState(getState(), containerId, { isLocked: false }));

				onSuccess?.('rankView');

				experience.navBar.changeViewRank.success();

				onActionSuccess?.();
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				// Reverse the changes in case of failure
				setState(
					updateContainerState(getState(), containerId, {
						views: previousViewsState,
						isLocked: false,
					}),
				);

				if (error instanceof Error) {
					onActionError?.(error);
				}

				onFailure?.(error, 'rankView');

				if (isClientFetchError(error)) {
					experience.navBar.changeViewRank.abort(error);
					return;
				}

				experience.navBar.changeViewRank.failure(error);
			}
		},
	);
